/**
 * Hooks React Query da equipe da própria clinic do owner/admin.
 *
 * RLS: `clinic_members_select_member` libera leitura quando user é
 * membro ativo. UPDATE/INSERT só passam pra `clinic_owner` ou
 * `clinic_admin` (validado tanto no banco quanto na edge function
 * `invite-clinic-member`).
 *
 * Mutations:
 *   - `useInviteClinicMember` — chama edge function que cria user +
 *     member + magic link + email.
 *   - `useUpdateMemberRole` — UPDATE direto via RLS.
 *   - `useSuspendMember` — UPDATE status='suspended'.
 *   - `useReactivateMember` — UPDATE status='active'.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

import type { InvitableRoleKey, InviteClinicMemberInput } from './schemas.js';

export const CLINIC_TEAM_KEYS = {
  members: (clinicId: string) => ['clinic-team', 'members', clinicId] as const,
  rolesCatalog: () => ['clinic-team', 'roles-catalog'] as const,
};

/** Linha de `clinic_members` enriquecida com email do user (via embed
 * de `auth.users` indireto via `profiles` que espelha auth.users +
 * full_name). */
export interface ClinicMemberRow {
  id: string;
  clinic_id: string;
  user_id: string;
  role_key: string;
  status: 'invited' | 'active' | 'suspended' | string;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
  /** Embed de `profiles` (1-to-1 com auth.users via id). Pode ser
   * null caso profile não tenha sido criado ainda. */
  profile: {
    id: string;
    email: string | null;
    full_name: string | null;
  } | null;
}

export function useClinicTeamMembers(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: CLINIC_TEAM_KEYS.members(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 15_000,
    queryFn: async (): Promise<ClinicMemberRow[]> => {
      if (!clinicId) return [];
      // Embed `profiles` via FK clinic_members.user_id → profiles.id.
      // Supabase JS narrow tipa nested como array — desempacotamos no
      // map abaixo.
      const { data, error } = await supabase
        .from('clinic_members')
        .select(
          'id, clinic_id, user_id, role_key, status, invited_at, accepted_at, created_at, profile:profiles!inner(id, email, full_name)',
        )
        .eq('clinic_id', clinicId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (
        (data ?? []) as unknown as {
          id: string;
          clinic_id: string;
          user_id: string;
          role_key: string;
          status: string;
          invited_at: string | null;
          accepted_at: string | null;
          created_at: string;
          profile:
            | { id: string; email: string | null; full_name: string | null }
            | { id: string; email: string | null; full_name: string | null }[]
            | null;
        }[]
      ).map((row) => ({
        ...row,
        profile: Array.isArray(row.profile) ? (row.profile[0] ?? null) : row.profile,
      })) as ClinicMemberRow[];
    },
  });
}

/** Catálogo de roles (read-only, raramente muda). 5min de cache. */
export interface RoleCatalogRow {
  key: string;
  name_pt_br: string;
  description: string | null;
  is_clinic_scoped: boolean;
  sort_order: number;
}

export function useRolesCatalog() {
  return useQuery({
    queryKey: CLINIC_TEAM_KEYS.rolesCatalog(),
    staleTime: 5 * 60 * 1_000,
    queryFn: async (): Promise<RoleCatalogRow[]> => {
      const { data, error } = await supabase
        .from('roles')
        .select('key, name_pt_br, description, is_clinic_scoped, sort_order')
        .eq('is_clinic_scoped', true)
        .order('sort_order', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as RoleCatalogRow[];
    },
  });
}

/**
 * Convida novo membro via edge function `invite-clinic-member`.
 * Edge function:
 *   1. Valida caller é owner/admin
 *   2. Cria/reusa user em auth.users
 *   3. Cria/reusa clinic_members (status='invited')
 *   4. Gera magic link de invite
 *   5. Envia email `welcome_clinic_member`
 *
 * Retorno:
 *   - `action: 'invited'` — primeiro convite
 *   - `action: 'already_invited'` — já tinha convite pendente; reenviou email
 *   - 409 — member já está `active` (não tem o que convidar)
 */
export interface InviteClinicMemberResult {
  status: 'ok';
  action: 'invited' | 'already_invited';
  clinic_member_id: string;
  user_id: string;
  email_status: 'sent' | 'failed';
  link_type: 'invite' | 'magiclink';
}

export function useInviteClinicMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: { clinic_id: string } & InviteClinicMemberInput,
    ): Promise<InviteClinicMemberResult> => {
      const { data, error } = await supabase.functions.invoke('invite-clinic-member', {
        body: input,
      });
      if (error) throw new Error(error.message);
      return data as InviteClinicMemberResult;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_TEAM_KEYS.members(variables.clinic_id) });
    },
  });
}

/** Muda role de member existente. RLS valida que caller é owner/admin. */
export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinic_id: string;
      member_id: string;
      role_key: InvitableRoleKey;
    }) => {
      const { error } = await supabase
        .from('clinic_members')
        .update({ role_key: input.role_key })
        .eq('id', input.member_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_TEAM_KEYS.members(variables.clinic_id) });
    },
  });
}

/** Suspende member (UPDATE status='suspended'). Soft delete — preserva
 * histórico. RLS valida owner/admin. Owner não pode se suspender (UI
 * bloqueia, banco permite — proteção é UX). */
export function useSuspendMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinic_id: string; member_id: string }) => {
      const { error } = await supabase
        .from('clinic_members')
        .update({ status: 'suspended' })
        .eq('id', input.member_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_TEAM_KEYS.members(variables.clinic_id) });
    },
  });
}

/** Reativa member suspenso (UPDATE status='active'). */
export function useReactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinic_id: string; member_id: string }) => {
      const { error } = await supabase
        .from('clinic_members')
        .update({ status: 'active' })
        .eq('id', input.member_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_TEAM_KEYS.members(variables.clinic_id) });
    },
  });
}
